import loadable from "@loadable/component"
import { FocusScope } from "@react-aria/focus"
import PropTypes from "prop-types"
import { useEffect, useRef } from "react"
import { Helmet } from "react-helmet-async"
import { useDispatch, useSelector } from "react-redux"

import { setExperiment } from "app-store-v2/app/actions"
import fetchProduct from "areas/product/hoc/fetch-product"
import withImages from "areas/product/hoc/with-images"
import ImageArea from "areas/product/image-area"
import { ProductImage } from "areas/product/jewlr/components"
import { trackSlide } from "areas/product/store/actions"
import SimpleDeferAfterLoadWrapper from "components/common/simple-defer-load-wrapper"
import Button from "components/ui/button"
import Media from "components/ui/media"
import { IS_JEWLR, retry } from "helpers/application"
import { isVideoView, isJ3dView } from "helpers/gallery-slide-view"
import { useNavigation } from "helpers/use-navigation"
import fetchWishlist from "hoc/fetch-wishlist"
import { cn } from "styles/utils"

import ImageAreaContent from "./image-area-content"
import MainBody from "./main-body"
import Breadcrumbs from "./wizard-header/breadcrumbs"
import OutOfStockBanner from "./wizard-header/out-of-stock-banner"

const GenericNotFound = loadable(() =>
  retry(() => import("app/generic-not-found"))
)
const ProductFooter = loadable(() => retry(() => import("./product-footer")), {
  ssr: false,
})
const CartPopup = loadable(() => retry(() => import("areas/cart/popup")))
const CartPreviewModal = loadable(() =>
  retry(() => import("areas/cart/preview"))
)
const HonestPricingModal = loadable(() =>
  retry(() => import("./modals/honest-pricing-modal"))
)
const ReturnPolicyModal = loadable(() =>
  retry(() => import("./modals/return-policy-modal"))
)
const PreviewImageModal = loadable(() =>
  retry(() => import("./modals/preview-image-modal"))
)

if (typeof window != "undefined") {
  try {
    if (window.location.hash != "") {
      var hash = window.location.hash.split("#")[1]
      if (hash.match("&gid=[0-9]*&pid=[0-9]*")) {
        window.location.hash = ""
      }
    }
  } catch (e) {
    console.log(e)
  }

  // EDGE ONLY - remove this line once Microsoft Edge decides to start supporting
  // HTMLCanvasElement.toBlob
  try {
    if (
      !Object.prototype.hasOwnProperty.call(
        HTMLCanvasElement.prototype,
        "toBlob"
      )
    ) {
      require("blueimp-canvas-to-blob")
    }
  } catch (e) {
    // Continue if this polyfill fails to load
    console.log("Couldn't load toBlob polyfill", e)
  }
}

const Thumbnail = ({
  index,
  onClick,
  productTitle,
  selected,
  src,
  srcSet,
  tabIndex,
  view,
}) => {
  const keyboardProps = useNavigation()

  return (
    <Button
      aria-controls={`image-slider-tabpanel-${index + 1}-main`}
      aria-label={`slide-${index + 1}`}
      aria-selected={selected}
      className={cn(
        "hover:border-grey-5 focus:border-primary border-3 relative block focus:outline-none",
        selected ? "border-grey-10" : "border-transparent"
      )}
      onClick={onClick}
      role="tab"
      selected={selected}
      tabIndex={tabIndex}
      {...keyboardProps}
    >
      <ProductImage
        alt={productTitle}
        className={cn(
          "mb-2 h-48 w-48",
          view === "tangiblee_view"
            ? sl.config.env == "staging"
              ? "js-tangiblee-cta-v2"
              : "js-tangiblee-cta"
            : ""
        )}
        data-cy={`product-main-slider-thumbnail-img-${index}`}
        height="500"
        lazyload={index > 0}
        sizes="48px"
        src={src}
        srcSet={srcSet}
        width="500"
      />
    </Button>
  )
}

Thumbnail.propTypes = {
  index: PropTypes.number,
  onClick: PropTypes.func,
  productTitle: PropTypes.string,
  selected: PropTypes.bool,
  src: PropTypes.string,
  srcSet: PropTypes.string,
  tabIndex: PropTypes.string,
  view: PropTypes.string,
}

/**
 * Product index
 * @param {object} data - default data from _react.html
 */
const InternalProduct = ({
  currentSlideIndex,
  j3dSlideIndex,
  productTitle,
  slidesArray,
  staticContext,
  styleCode,
  wizardLoading,
}) => {
  const wizardBodyRef = useRef(null)
  const outOfStockRef = useRef()
  const dispatch = useDispatch()
  const outOfStock = useSelector(
    (state) => state.wizard.header?.product_out_of_stock
  )
  const productNotFound = useSelector((state) => state.wizardUI.productNotFound)
  const stickyATCExp = useSelector((state) => state.experiment.stickyATC)

  const ssrNotFound =
    staticContext && styleCode === undefined && outOfStock === undefined

  useEffect(() => {
    if (stickyATCExp) {
      dispatch(
        setExperiment({
          stickyATC: stickyATCExp,
        })
      )
    }
  }, [stickyATCExp])

  if (productNotFound || ssrNotFound) {
    return <GenericNotFound staticContext={staticContext} />
  }

  return (
    <>
      <Helmet encodeSpecialCharacters={false}>
        {/* eslint-disable react/jsx-sort-props, react/no-unknown-property */}
        <meta content="240" name="og_image_width" property="og:image:width" />
        <meta content="240" name="og_image_height" property="og:image:height" />
        {slidesArray?.length > 0 && slidesArray[0].src && (
          <meta
            content={slidesArray[0].src}
            name="og_image"
            property="og:image"
          />
        )}
        {slidesArray?.length > 0 && slidesArray[0].srcset && (
          <link
            rel="preload"
            fetchPriority="high"
            as="image"
            imageSrcSet={slidesArray[0].srcset}
            imageSizes={slidesArray[0].sizes}
          />
        )}
        {/* eslint-enable react/jsx-sort-props, react/no-unknown-property */}
      </Helmet>
      <div className="tablet:px-16 tablet:mb-10 tablet:pt-32 tablet:max-w-835 desktop:max-w-full min-[1568px]:!max-w-1440 mx-auto">
        {outOfStock && (
          <Media className="mb-24 px-20" lessThan="tablet">
            <OutOfStockBanner outOfStockRef={outOfStockRef} />
          </Media>
        )}
        <div className="tablet:items-start tablet:flex-row flex flex-col">
          <div className="tablet:mb-16 tablet:pr-40 tablet:w-1/2 flex-1">
            <ImageArea wizardBodyRef={wizardLoading ? {} : wizardBodyRef}>
              <div className="flex gap-44">
                <Media
                  aria-label="Product Images"
                  className="desktop:flex hidden min-w-52 flex-col gap-12"
                  greaterThanOrEqual="desktop"
                  role="tablist"
                >
                  <FocusScope>
                    {slidesArray?.length > 0 &&
                      slidesArray
                        .filter(({ view }) => !isJ3dView(view))
                        .map((slide, index) => (
                          <Thumbnail
                            index={index}
                            key={`mainthumb-${slide.view}`}
                            onClick={() => dispatch(trackSlide(index))}
                            productTitle={productTitle}
                            selected={currentSlideIndex === index}
                            src={
                              isVideoView(slide.view)
                                ? require("images/product/vr/videoicon.png")
                                : slide.src
                            }
                            {...(!isVideoView(slide.view) && {
                              srcSet: slide.srcset,
                            })}
                            tabIndex={
                              currentSlideIndex === index ||
                              (currentSlideIndex === j3dSlideIndex &&
                                index === 0)
                                ? "0"
                                : "-1"
                            }
                            view={slide.view}
                          />
                        ))}
                  </FocusScope>
                </Media>
                <ImageAreaContent />
              </div>
            </ImageArea>
          </div>
          <div
            className="tablet:w-1/2 desktop:w-480 w-full px-16"
            ref={wizardBodyRef}
          >
            <Breadcrumbs />
            <MainBody outOfStockRef={outOfStockRef} />
          </div>
        </div>
      </div>
      <ProductFooter />
      <SimpleDeferAfterLoadWrapper>
        <CartPopup />
        <CartPreviewModal />
        <PreviewImageModal />
        {IS_JEWLR() && <HonestPricingModal />}
        {IS_JEWLR() && <ReturnPolicyModal />}
      </SimpleDeferAfterLoadWrapper>
    </>
  )
}

InternalProduct.propTypes = {
  currentSlideIndex: PropTypes.number,
  j3dSlideIndex: PropTypes.number,
  productTitle: PropTypes.string,
  slidesArray: PropTypes.array,
  staticContext: PropTypes.object,
  styleCode: PropTypes.string,
  trackSlide: PropTypes.func,
  updateChoice: PropTypes.func,
  wizardLoading: PropTypes.string,
}

const Product = fetchWishlist(InternalProduct)

export default fetchProduct(withImages(Product))
